import { useState, useEffect } from "react";
import SectionTitle from "../../components/SectionTitle";
import RevenueDetailsTable from "../../components/revenue/RevenueDetailsTable";
import RevenueLigthSection from "../../components/revenue/RevenueLigthSection";
import RevenueBaseSection from "../../components/revenue/RevenueBaseSection";
import RevenueRealSection from "../../components/revenue/RevenueRealSection";
import { trpc } from "../../trpc";
import { Spinner } from "@/components/ui/spinner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RevenueContainer = () => {
  const [selectedYear, setSelectedYear] = useState<string>("2026");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [trmValue, setTrmValue] = useState<number>(4000);
  const [revenueType, setRevenueType] = useState<string>("all");

  const { data: realRevenueData, isLoading: isRealRevenueLoading } =
    trpc.revenue.realRevenue.useQuery(
      {
        year: parseInt(selectedYear),
        month: parseInt(selectedMonth),
        revenueType: revenueType === "all" ? undefined : revenueType,
      },
      {
        enabled: !!selectedMonth && !!selectedYear,
      }
    );

  useEffect(() => {
    const currentMonth = new Date().getMonth() + 1;
    setSelectedMonth(currentMonth.toString().padStart(2, "0"));
  }, []);

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="p-6 mx-auto w-full">
      <div className="flex flex-col gap-6 w-full">
        <SectionTitle 
          title="Revenue Dashboard" 
          breadcrumbs={[
            { label: "Admin", href: "/" },
            { label: "Revenue" }
          ]}
          description="View and analyze revenue metrics"
        />

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="year">Year:</Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="month">Month:</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="trm">TRM (USD/COP):</Label>
            <Input
              id="trm"
              type="number"
              value={trmValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrmValue(parseInt(e.target.value))}
              className="w-[150px]"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="revenueType">Revenue Type:</Label>
            <Select value={revenueType} onValueChange={setRevenueType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="rentals">Rentals</SelectItem>
                <SelectItem value="development">Development</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isRealRevenueLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-6 w-full">
            <RevenueBaseSection
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              trmValue={trmValue}
              formatCurrency={formatCurrency}
              formatUSD={formatUSD}
              revenueType={revenueType}
            />

            <RevenueRealSection
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              trmValue={trmValue}
              formatCurrency={formatCurrency}
              formatUSD={formatUSD}
              revenueType={revenueType}
            />

            <RevenueLigthSection
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              trmValue={trmValue}
              formatCurrency={formatCurrency}
              formatUSD={formatUSD}
              revenueType={revenueType}
            />

            {realRevenueData?.detail && realRevenueData.detail.length > 0 && (
              <RevenueDetailsTable data={realRevenueData.detail} trmValue={trmValue} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueContainer;
